import {ComponentType, createContext, useContext} from 'react';
import {
  ClientResponse,
  CustomPartConversationMessage,
  MessagePartData,
  MessagePartActionParams,
  MessagePartSubmitParams,
} from '@stripe-internal/conversations-client';
import {MessageComponentProps} from './MessageComponentContext';

export type MessagePartOnAction = (
  params: MessagePartActionParams,
) => Promise<ClientResponse>;

export type MessagePartSubmission = MessagePartSubmitParams['submission'];

export type MessagePartOnSubmit = (
  params: MessagePartSubmitParams,
) => Promise<ClientResponse>;

/**
 * Describes a message-part submission payload sent back to the server.
 * Field names align with `ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContent`.
 * TODO: add type generation to not need to manually define this type def
 */
export type ChannelMenuSubmissionContent = {
  text: string;
  channel: 'channel_chat' | 'channel_restart' | 'channel_rac' | 'channel_email';
  analytics_event_id?: string;
  rac_form?: {
    name: string;
    phone_number: string;
    message: string;
  };
  email_form?: {
    email: string;
    body: string;
    subject?: string;
    cc_emails?: string[];
    attachment_ids?: string[];
    request_issue_context?: Record<string, unknown>;
  };
};

export type ChannelMenuSubmissionPart = {
  type: 'channel_menu_submission';
  content: ChannelMenuSubmissionContent;
};

export type MessagePartHandlers = {
  onAction?: MessagePartOnAction;
  onSubmit?: MessagePartOnSubmit;
};

export type MessagePartComponentProps<
  TMessage extends CustomPartConversationMessage = CustomPartConversationMessage,
  TPart extends MessagePartData = MessagePartData,
> = Omit<MessageComponentProps, 'message'> & {
  message: TMessage;
  part: TPart;
};

export type MessagePartComponentRegistry = Record<
  string,
  ComponentType<MessagePartComponentProps> | undefined
>;

export const MessagePartComponentContext =
  createContext<MessagePartComponentRegistry>({});

export function useMessagePartComponents() {
  return useContext(MessagePartComponentContext);
}
